import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import axios from 'axios' // zah
import api from '../lib/api'
import { supabase } from '../lib/supabase'
import {
  forceLocalSignOut,
  handleAuthError,
  dispatchAuthError,
  fetchWithAuthRetry,
} from '../lib/authErrorHandlers'
import { refreshSessionOnce, tryRefreshAndReplay } from '../lib/sessionRefresh'

// Global auth chokepoint on the default axios singleton. Delegates to
// dispatchAuthError in authErrorHandlers.js — the single source of truth
// for (status, code) → handler routing. Kept in lockstep with lib/api.js
// (api instance) and authErrorHandlers.js handleAuthError (native fetch).
//
// State → wire shape → destination (full table in dispatchAuthError):
//   401 SESSION_INVALIDATED → /login?reason=session_invalidated (toast + signOut)
//   401 NO_AUTH             → /login?reason=login_required      (no signOut)
//   401 AUTH_INVALID        → /login?reason=session_expired     (signOut)
//   503 AUTH_TRANSIENT      → toast (no signOut, no redirect)
//   403 BANNED              → /login?reason=banned               (signOut)
//   403 FROZEN              → /                                  (no signOut — SuspensionGate needs the session)
//   403 SUSPENDED           → /                                  (no signOut — same reason)
//   403 PENDING_DELETION    → /account/restore                   (no signOut)
//   401 (no code)           → /login?reason=session_expired      (legacy fallback, signOut)
//   403 EMAIL_NOT_VERIFIED  → pass through to existing VerifyEmailGate
//
// Always re-rejects the error so per-form .catch() handlers still fire and
// surface inline error state before the page tears down. Registered once at
// module load to avoid double-fire on StrictMode remounts.
axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Stale-token recovery (mirrors lib/api.js via the shared helper). The
    // default singleton has NO request interceptor, so tryRefreshAndReplay
    // overwrites the stale Authorization header with the fresh token before
    // replaying. Returns the replayed response on success; null when not
    // eligible or the refresh failed → fall through to the existing dispatch.
    const replayed = await tryRefreshAndReplay(err, axios)
    if (replayed) return replayed
    dispatchAuthError(err?.response?.status, err?.response?.data?.code, err?.response?.data)
    return Promise.reject(err)
  }
)

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showSignInAnimation, setShowSignInAnimation] = useState(false)

  async function fetchProfile() {
    try {
      // Read the token fresh at call time (the useNotifications poller
      // pattern). getSession() refreshes an expired token before returning;
      // the onAuthStateChange event-closure token can be stale on
      // INITIAL_SESSION and 401 a perfectly refreshable session — confirmed
      // in prod 2026-06-12 (log-ip teardown incident).
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) return
      const res = await fetchWithAuthRetry('/api/profile/', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        // Route auth-rejection states through the shared chokepoint —
        // SESSION_INVALIDATED, BANNED, FROZEN, SUSPENDED, bare 401.
        // EMAIL_NOT_VERIFIED + other codes pass through; profile load
        // just no-ops in those cases.
        await handleAuthError(res)
        return
      }
      const data = await res.json()
      setProfile(data)
      // Sync accent theme from server profile
      if (data.accentTheme) {
        localStorage.setItem('verse_accent', data.accentTheme)
        window.dispatchEvent(new CustomEvent('verse-accent-sync', { detail: data.accentTheme }))
      }
    } catch (err) { console.error('[AuthContext] Failed to fetch profile:', err) }
  }

  useEffect(() => {
    let initialised = false

    // Keep in sync with Supabase auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null)
        if (!initialised) { initialised = true; setLoading(false) }
        // Fire sign-in animation on any auth transition that produces a
        // session, but only when preceded by an explicit user-initiated
        // sign-in click (which sets verse_signin_intent). Page refreshes,
        // token refreshes, and tab-focus events never set the flag so they
        // can't trigger the animation.
        if (session?.user) {
          try {
            if (sessionStorage.getItem('verse_signin_intent') === '1') {
              sessionStorage.removeItem('verse_signin_intent')
              setShowSignInAnimation(true)
            }
          } catch {}
        }
        if (!session?.access_token) { setProfile(null); return }
        fetchProfile()
        // Step 5 (mig 191) revoked client-direct INSERT/UPDATE on
        // public.profiles. handle_new_user trigger is the authoritative
        // writer at signup; no client-side upsert is needed.
        if (_event === 'INITIAL_SESSION' || _event === 'TOKEN_REFRESHED') {
          // Log IP for ban evasion detection (non-blocking). Peek (read-only,
          // no removeItem) at verse_signup_pending to distinguish a fresh
          // post-verify-click signup from a regular sign-in. The Step 2
          // deferred-call block below removes the key. OAuth signups log
          // as 'login' (the verse_oauth_dob mechanism was deleted in Step 3).
          if (_event === 'INITIAL_SESSION') {
            let isSignup = false
            try { isSignup = !!sessionStorage.getItem('verse_signup_pending') } catch {}
            // api instance: its request interceptor reads a fresh token via
            // getSession() (which refreshes an expired one) — the event-closure
            // session.access_token can be stale on INITIAL_SESSION and 401.
            // skipAuthDispatch: a failure on this fire-and-forget ping must
            // never escalate to signOut/redirect (prod incident 2026-06-12).
            api.post('/api/auth/log-ip', { action: isSignup ? 'signup' : 'login' }, {
              skipAuthDispatch: true,
            }).catch(() => {})
          }

          // Step 2 of signup-flow plan: drain the deferred username write
          // for email-password signups. Signup.jsx persists the user-typed
          // value into sessionStorage at signUp time; we fire it here once
          // the user's verify-email click has established a session. Gated on
          // email_confirmed_at so a pre-verification page reload doesn't fire
          // (though both endpoints are in EMAIL_VERIFY_EXEMPT, the user has no
          // session yet in that state). Idempotent via removeItem — re-firing
          // on TOKEN_REFRESHED finds the key empty and short-circuits.
          if (session.user.email_confirmed_at) {
            let pending = null
            try {
              const raw = sessionStorage.getItem('verse_signup_pending')
              if (raw) {
                pending = JSON.parse(raw)
                sessionStorage.removeItem('verse_signup_pending')
              }
            } catch (err) {
              console.error('[AuthContext] Failed to read signup-pending state:', err)
            }
            if (pending) {
              // PATCH /username — handle_new_user writes username from
              // signUp metadata.username, so this is an idempotent no-op
              // for the username field; it does, however, set
              // last_username_change which puts the user on the 30-day
              // cooldown immediately. Acceptable for Step 2.
              // api instance + skipAuthDispatch: same stale-closure-token
              // class as the log-ip ping — a 401 here in the fresh-signup
              // bootstrap window must not tear down the brand-new session
              // (2026-05-13 regression class). Failure stays console.warn.
              if (pending.username) {
                api.patch('/api/user/username', { username: pending.username }, {
                  skipAuthDispatch: true,
                }).catch((err) => {
                  console.warn('[signup-defer] username PATCH failed:', err.response?.data?.error ?? err.message)
                })
              }
            }
          }
        }
      }
    )

    // Safety: if onAuthStateChange hasn't fired within 500ms, stop loading
    const timeout = setTimeout(() => { if (!initialised) { initialised = true; setLoading(false) } }, 500)

    return () => { subscription.unsubscribe(); clearTimeout(timeout) }
  }, [])

  // Proactive token refresh on tab foreground. iOS Safari throttles the SDK's
  // background auto-refresh on a backgrounded tab; on resume the 60s pollers
  // (system-messages, profile heartbeat) can fire a stale token → 401 →
  // logout. Pre-warming the session on visibility → visible means the reactive
  // refresh-and-retry rarely needs to fire. Only refreshes (never dispatches a
  // logout); debounced to once per 30s so rapid visibility toggles don't spam.
  useEffect(() => {
    if (!user) return
    let lastRun = 0
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastRun < 30_000) return
      lastRun = now
      refreshSessionOnce()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [user?.id])

  const signIn = useCallback(async (email, password, captchaToken = null) => {
    try { sessionStorage.setItem('verse_signin_intent', '1') } catch {}
    const { data, error } = await supabase.auth.signInWithPassword({ email, password, options: captchaToken ? { captchaToken } : undefined })
    if (error) { try { sessionStorage.removeItem('verse_signin_intent') } catch {} }
    if (data?.user && !error) {
      if (data.session?.access_token) {
        await fetchProfile()
        api.post('/api/auth/log-ip', { action: 'login' }, { skipAuthDispatch: true }).catch(() => {})
      }
    }
    return { data, error }
  }, [])

  const signUp = useCallback(async (email, password, username, dob = null, accountType = 'adult', captchaToken = null) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username, dob }, ...(captchaToken ? { captchaToken } : {}) },
    })
    return { data, error }
  }, [])

  const signOut = useCallback(async () => {
    // Happy path: SDK signOut succeeds, fires SIGNED_OUT, the listener at the
    // top of this provider clears user/profile, and route guards navigate
    // to /login. No explicit nav needed here.
    //
    // Failure path: GoTrue rejects signOut for users with banned_until set
    // (and other failure modes — offline, GoTrue down, refresh-token already
    // revoked server-side). Without a fallback, a banned user is trapped on
    // the site until their access token expires (~1 hour by default). Any
    // signOut failure → fall through to a hard local logout: clear all
    // Supabase-managed storage so the SDK can't resurrect the session on
    // next page load, clear in-memory state, and force a full reload to
    // /login.
    let signOutError = null
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' })
      signOutError = error
    } catch (err) {
      signOutError = err
    }
    if (!signOutError) return

    console.warn('[auth] signOut failed; falling back to hard logout:', signOutError?.message ?? signOutError)
    forceLocalSignOut()
    setUser(null)
    setProfile(null)
    // window.location.href forces a full reload, dropping any cached fetches,
    // axios interceptors holding the old token, in-memory state in unrelated
    // providers, etc. router-navigate would leave that state intact.
    window.location.href = '/login'
  }, [])

  const getToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token ?? null
  }, [])

  // Re-fetch the server profile on demand. Used by DobGate after a username
  // write so nav/avatar drop the placeholder without a full reload.
  const refreshProfile = useCallback(async () => { await fetchProfile() }, [])

  const dismissSignInAnimation = useCallback(() => setShowSignInAnimation(false), [])

  const stableUser = useMemo(() => user, [user?.id, user?.email_confirmed_at])
  const stableProfile = useMemo(() => profile, [profile?.id])
  const emailVerified = Boolean(user?.email_confirmed_at)

  const value = useMemo(() => ({
    user: stableUser, profile: stableProfile, loading, emailVerified, signIn, signUp, signOut, getToken,
    refreshProfile, showSignInAnimation, dismissSignInAnimation,
  }), [stableUser, stableProfile, loading, emailVerified, signIn, signUp, signOut, getToken, refreshProfile, showSignInAnimation, dismissSignInAnimation])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}

const DEFAULT_GRAD = 'linear-gradient(135deg, #7C3AED, #EC4899)'

/** Derive display-ready user info from a raw Supabase auth user + optional profile.
 *
 * PR 2: dropped the user_metadata.username fallback layer. Source of truth is
 * profile.username (server-authoritative via handle_new_user trigger, migration
 * 127). Email-local fallback covers the brief window between sign-in and
 * profile fetch. */
export function displayUser(user, profile = null) {
  if (!user) return null
  const username    = profile?.username ?? user.email?.split('@')[0] ?? 'writer'
  const displayName = profile?.displayName ?? username
  return {
    username,
    displayName,
    initial:   displayName[0].toUpperCase(),
    grad:      profile?.avatarGrad ?? DEFAULT_GRAD,
    avatarUrl: profile?.avatarUrl ?? null,
  }
}

/** Returns true if the user has a teen account (age 13-17).
 *
 * PR 2: reads profile.accountType (server-authoritative via normaliseProfile,
 * written from profiles.account_type by handle_new_user trigger, migration 127).
 * Previously read user.user_metadata.account_type which was client-writable and
 * bypassed every UI gate — see audit Phase 4 transcript.
 *
 * Fail-OPEN on missing profile (returns false → not-teen). Rationale:
 * this helper drives UX affordances (hide Messages tab, etc.) during the
 * brief window after sign-in before fetchProfile resolves. Showing the tab
 * briefly to a user whose profile hasn't loaded is better than hiding it
 * from adults whose profile is still loading. MatureContentGate and
 * isMatureAllowed use the opposite (fail-closed) posture for the
 * security-critical content gate. */
export function isTeenAccount(profile) {
  return profile?.accountType === 'teen'
}

/** Returns true if the user's account is restricted (view-only mode). */
export function isRestricted(profile) {
  return profile?.restricted === true
}

/** Returns true if the user is allowed to view mature content.
 *
 * PR 2: fail-CLOSED on missing profile (F6 audit finding). Previously
 * returned true when user_metadata.account_type was absent, which meant any
 * user who erased their metadata (via updateUser or F2 username-change
 * clobber) was treated as adult. Now: only 'adult' passes. */
export function isMatureAllowed(profile) {
  return profile?.accountType === 'adult'
}
